'use client';

export const dynamic = 'force-dynamic';

import React, { useEffect, useState } from 'react';
import {
	useRouter,
	useSearchParams,
} from 'next/navigation';
import Link from 'next/link';
import api from '@/utils/api';
import { useAuth } from '@/contexts/AuthContext';
import { ImagePlus, CheckCircle, Home } from 'lucide-react'; // Added icons
import { AxiosProgressEvent } from 'axios';

const BRAND = {
	primary: '#051449',
	accent: '#FFCF23',
	soft: '#D2DFFF',
	bg: '#F8ECC1',
};

const MAX_IMAGE_SIZE = 5 * 1024 * 1024; // 5MB
const ALLOWED_IMAGE_TYPES = [
	'image/jpeg',
	'image/jpg',
	'image/png',
];

export default function BecomeVendorPage() {
	const {
		user,
		loading: authLoading,
		refreshUser,
	} = useAuth();
	const router = useRouter();
	const params = useSearchParams();

	// form state
	const [matricNumber, setMatricNumber] = useState('');
	const [bvn, setBvn] = useState(''); // Note: BVN input was missing in previous JSX, ensuring it's kept in state if needed or removed if unused based on your backend. Kept state for safety.
	const [idFrontFile, setIdFrontFile] =
		useState<File | null>(null);
	const [idBackFile, setIdBackFile] = useState<File | null>(
		null,
	);
	const [idFrontPreview, setIdFrontPreview] = useState<
		string | null
	>(null);
	const [idBackPreview, setIdBackPreview] = useState<
		string | null
	>(null);

	const [error, setError] = useState<string | null>(null);
	const [busy, setBusy] = useState(false);
	const [progress, setProgress] = useState<number>(0);

	// New state for Success Modal
	const [showSuccessModal, setShowSuccessModal] =
		useState(false);

	// redirect already verified or vendor users away from the form
	useEffect(() => {
		if (!authLoading && user) {
			if (user.role === 'vendor' || user.verified) {
				router.push('/vendor/create');
			}
		}
	}, [authLoading, user, router]);

	// previews
	useEffect(() => {
		if (idFrontFile) {
			const url = URL.createObjectURL(idFrontFile);
			setIdFrontPreview(url);
			return () => URL.revokeObjectURL(url);
		}
		setIdFrontPreview(null);
	}, [idFrontFile]);

	useEffect(() => {
		if (idBackFile) {
			const url = URL.createObjectURL(idBackFile);
			setIdBackPreview(url);
			return () => URL.revokeObjectURL(url);
		}
		setIdBackPreview(null);
	}, [idBackFile]);

	function validate() {
		if (!matricNumber.trim())
			return 'Matric number is required.';

		if (!idFrontFile || !idBackFile)
			return 'Upload both front and back of your student ID.';
		return null;
	}

	function validateFile(file: File | null) {
		if (!file) return 'No file supplied';
		if (!ALLOWED_IMAGE_TYPES.includes(file.type))
			return 'Only JPG/PNG images are allowed';
		if (file.size > MAX_IMAGE_SIZE)
			return `File too large (max ${Math.round(
				MAX_IMAGE_SIZE / 1024 / 1024,
			)}MB)`;
		return null;
	}

	const handleFrontChange = (f: File | null) => {
		if (!f) {
			setIdFrontFile(null);
			return;
		}
		const v = validateFile(f);
		if (v) {
			setError(`Front ID: ${v}`);
			return;
		}
		setError(null);
		setIdFrontFile(f);
	};

	const handleBackChange = (f: File | null) => {
		if (!f) {
			setIdBackFile(null);
			return;
		}
		const v = validateFile(f);
		if (v) {
			setError(`Back ID: ${v}`);
			return;
		}
		setError(null);
		setIdBackFile(f);
	};

	async function submitApplication(e?: React.FormEvent) {
		e?.preventDefault();
		setError(null);
		setProgress(0);

		const v = validate();
		if (v) {
			setError(v);
			return;
		}
		if (!user) {
			setError(
				'You must be logged in to apply. Please sign in or register.',
			);
			return;
		}

		setBusy(true); // Start busy state

		try {
			const form = new FormData();
			form.append('matricNumber', matricNumber);
			// form.append('bvn', bvn); // Uncomment if BVN is actually required in form input
			if (idFrontFile)
				form.append('idCardFront', idFrontFile);
			if (idBackFile) form.append('idCardBack', idBackFile);

			await api.post('/verifications/submit', form, {
				withCredentials: true,
				onUploadProgress: (evt?: AxiosProgressEvent) => {
					const loaded = evt?.loaded ?? 0;
					const total = evt?.total ?? 0;
					if (total > 0) {
						setProgress(Math.round((loaded / total) * 100));
					}
				},
			});

			// Success handling
			setProgress(100);

			// Stop busy state immediately and show modal
			setBusy(false);
			setShowSuccessModal(true);
		} catch (err: any) {
			console.error('🚨 [submitApplication] Error:', err);
			setError(
				err?.response?.data?.message ||
					err?.message ||
					'Application failed',
			);
			setBusy(false); // Stop busy state on error
			setTimeout(() => setProgress(0), 1200);
		}
	}

	// Unauthenticated UI
	if (!user) {
		const loginUrl = `/auth/login?redirectTo=${encodeURIComponent(
			'/become-vendor',
		)}`;
		const registerUrl = `/auth/register?redirectTo=${encodeURIComponent(
			'/become-vendor',
		)}`;

		return (
			<div className="min-h-[70vh] flex items-center justify-center px-4">
				<div className="max-w-3xl w-full bg-white p-8 rounded-xl shadow">
					<h1
						className="text-2xl font-semibold"
						style={{ color: BRAND.primary }}
					>
						Become a Vendor on Yunimall
					</h1>
					<p className="mt-2 text-gray-600">
						Sell to your university community. Create
						stores, list products and grow your student
						business.
					</p>

					<div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
						<Link
							href={loginUrl}
							className="block text-center py-3 rounded-md"
							style={{
								background: BRAND.accent,
								color: BRAND.primary,
							}}
						>
							Sign in to apply
						</Link>
						<Link
							href={registerUrl}
							className="block text-center py-3 rounded-md border"
							style={{ borderColor: BRAND.soft }}
						>
							Create an account
						</Link>
					</div>

					<div className="mt-6 text-sm text-gray-500">
						Already have an account?{' '}
						<Link href={loginUrl} className="underline">
							Sign in
						</Link>
					</div>
				</div>
			</div>
		);
	}

	// Authenticated form UI
	return (
		<div className="min-h-[70vh] flex items-center justify-center px-4 py-8 relative">
			{/* SUCCESS POPUP MODAL */}
			{showSuccessModal && (
				<div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
					<div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 text-center transform scale-100 animate-in zoom-in-95 duration-200">
						<div className="mx-auto flex items-center justify-center h-20 w-20 rounded-full bg-green-100 mb-6">
							<CheckCircle className="h-10 w-10 text-green-600" />
						</div>
						<h3 className="text-2xl font-bold text-gray-900 mb-2">
							Application Submitted!
						</h3>
						<p className="text-gray-600 mb-8">
							We have received your details. Our team will
							review your application within 48 hours and you will be
							notified once verified.
						</p>
						<button
							onClick={() => router.push('/')}
							className="w-full inline-flex justify-center items-center px-4 py-3 border border-transparent text-base font-medium rounded-xl text-white bg-blue-900 hover:bg-blue-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
						>
							<Home className="w-5 h-5 mr-2" />
							Return to Homepage
						</button>
					</div>
				</div>
			)}

			<div className="max-w-3xl w-full bg-white p-8 rounded-xl shadow">
				<div className="flex items-center justify-between mb-4">
					<div>
						<h1
							className="text-2xl font-semibold"
							style={{ color: BRAND.primary }}
						>
							Apply to become a vendor
						</h1>
						<p className="text-sm text-gray-600">
							We're excited to get you selling on Yunimall.
							Provide the info below to begin verification.
						</p>
					</div>

					{user && (
						<div className="text-sm text-gray-700 hidden sm:block">
							Logged in as <strong>{user.name}</strong>
							<div className="text-xs text-gray-500 mt-1">
								{user.email}
							</div>
						</div>
					)}
				</div>

				{error && (
					<div
						className="mb-4 p-3 rounded text-sm bg-[#ffeeda]"
						style={{ border: `1px solid ${BRAND.soft}` }}
					>
						{error}
					</div>
				)}

				<form
					onSubmit={submitApplication}
					className="space-y-4"
				>
					<div>
						<label className="block text-sm font-medium mb-1">
							Matriculation number
						</label>
						<input
							value={matricNumber}
							onChange={(e) =>
								setMatricNumber(e.target.value)
							}
							className="w-full p-3 border rounded focus:outline-none focus:ring-2 focus:ring-[#D2DFFF]"
							placeholder="e.g. 18/XXXXX"
							disabled={busy}
						/>
					</div>

					<div className="grid grid-cols-1 mt-5 sm:grid-cols-2 gap-4">
						<div>
							<label className="text-sm flex flex-row gap-2 items-center font-medium mb-1">
								<ImagePlus className="w-4 h-4" />
								<p>Student ID (front)</p>
							</label>
							<input
								type="file"
								accept="image/*"
								onChange={(e) =>
									handleFrontChange(
										e.target.files?.[0] || null,
									)
								}
								className="border py-2 px-3 rounded w-full cursor-pointer text-sm"
								disabled={busy}
							/>
							{idFrontPreview && (
								<div className="mt-2 border rounded p-1 inline-block">
									<img
										src={idFrontPreview}
										alt="id front"
										className="w-40 h-28 object-cover rounded"
									/>
								</div>
							)}
						</div>

						<div>
							<label className="text-sm flex flex-row gap-2 items-center font-medium mb-1">
								<ImagePlus className="w-4 h-4" />
								<p>Student ID (back)</p>
							</label>
							<input
								type="file"
								accept="image/*"
								onChange={(e) =>
									handleBackChange(
										e.target.files?.[0] || null,
									)
								}
								className="border py-2 px-3 rounded w-full cursor-pointer text-sm"
								disabled={busy}
							/>
							{idBackPreview && (
								<div className="mt-2 border rounded p-1 inline-block">
									<img
										src={idBackPreview}
										alt="id back"
										className="w-40 h-28 object-cover rounded"
									/>
								</div>
							)}
						</div>
					</div>

					{progress > 0 && progress < 100 && (
						<div className="w-full bg-gray-100 rounded h-2 overflow-hidden mt-4">
							<div
								className="h-2 transition-all duration-300"
								style={{
									width: `${progress}%`,
									background: BRAND.accent,
								}}
							/>
						</div>
					)}

					<div className="flex items-center gap-3 mt-8">
						<button
							disabled={busy}
							type="submit"
							className="w-full sm:w-auto px-8 py-3 rounded-lg font-bold shadow-lg transition-transform active:scale-95 disabled:opacity-70 disabled:cursor-not-allowed"
							style={{
								background: BRAND.accent,
								color: BRAND.primary,
							}}
						>
							{busy
								? 'Submitting...'
								: 'Submit Application'}
						</button>
					</div>

					<div className="text-xs text-gray-500 mt-4">
						By applying you agree to our{' '}
						<Link
							href="/terms-and-conditions"
							className="underline hover:text-blue-800"
						>
							Terms
						</Link>{' '}
						and{' '}
						<Link
							href="/privacy-policy"
							className="underline hover:text-blue-800"
						>
							Privacy Policy
						</Link>
						. Your ID will be used only to verify identity
						and will not be shared.
					</div>
				</form>
			</div>
		</div>
	);
}
